// External Dependencies:
import React from 'react'
import { connect } from 'react-redux'
import { withRouter, Link } from 'react-router-dom'
import { DateTime } from 'luxon'

// Internal Dependencies:
import SiteNotice from 'components/SiteNotice/SiteNotice'
import { getUtcTimestampSeconds } from 'utilities/time'

// Redux:
import { likeProgram, showProgramMenu, showSiteNotice } from 'actions/site'
import { setIsPlaying, setCustomNowPlaying } from 'actions/schedule'

class ProgramTrack extends React.Component {
	constructor( props ) {
		super( props )

		this.state = {
			clickedLike: false,
			showLikeNotice: false,
			showFirstLikeNotice: false,
			playingPodcast: false,
			playingPodcastFile: false
		}
	}

	handleShowMenuClick = () => {
		const { dispatch, data, nowPlaying, isPlayingGlobal, isFavourites, isPodcast, programData, isMobile } = this.props

		const { playingPodcast, playingPodcastFile } = this.state
		let isPlaying = ( playingPodcast && playingPodcastFile == data.url )
		const source = isFavourites ? 'favourites' : ''

		if ( nowPlaying.is_playlist && nowPlaying.audio_file_url == data.url ) {
			isPlaying = isPlayingGlobal
		}

		// If already playing, toggle the pause:
		if ( isPlaying ) {
			this.handlePlayItemClick()

			return
		}

		dispatch( showProgramMenu( {
			key: data.url,
			audio_file_url: data.url,
			title: data.title,
			series: data.series,
			series_slug: data.series_slug,
			guests: data.guests,
			number: data.number,
			last_played_timestamp_gmt: data.last_played_timestamp_gmt,
			playCallback: this.handlePlayItemClick,
			isPodcaast: ( isPodcast || programData.podcasts_enabled )
		}, source ) )
	}

	handlePlayItemClick = () => {
		const { history, dispatch, data, programData, nowPlaying, playlist, isFavourites, isRecent, isPodcast, rewindMaxHours, timezone, shareLinkData, isMobile } = this.props
		let { isShare } = this.props

		if ( ! isShare && shareLinkData && shareLinkData.file_name == data.audio_file_url ) {
			isShare = true
		}

		const utcTimestampSeconds = getUtcTimestampSeconds()
		let lastPlayedSecondsAgo = utcTimestampSeconds - parseInt( data.last_played_timestamp_gmt, 10 )
		let lastPlayedHoursAgo = lastPlayedSecondsAgo / 60 / 60
		const canPlay = ( isShare || programData.podcasts_enabled || lastPlayedHoursAgo <= rewindMaxHours )

		if ( ! canPlay ) {
			let dateStringLast = DateTime.fromMillis(
				( utcTimestampSeconds - lastPlayedSecondsAgo ) * 1000,
				{ zone: timezone }
			).toFormat( 'LLL dd' )

			if ( rewindMaxHours < 48 ) {
				dispatch( showSiteNotice( {
					text: `This episode last aired on ${dateStringLast} and was available for ${rewindMaxHours} hours. Earn more badges to extend availability of past episodes.`,
					cancelText: 'OK'
				} ) )
			} else {
				const rewindMaxHoursInSeconds = rewindMaxHours * 60 * 60
				let dateStringUntil = DateTime.fromMillis(
					( utcTimestampSeconds - lastPlayedSecondsAgo + rewindMaxHoursInSeconds ) * 1000,
					{ zone: timezone }
				).toFormat( 'LLL dd' )

				dispatch( showSiteNotice( {
					text: `This episode last aired on ${dateStringLast} and was available until ${dateStringUntil}. Earn more badges to extend availability of past episodes.`,
					cancelText: 'OK'
				} ) )
			}

			return
		}

		const playingPodcast = ( data.url == this.state.playingPodcastFile ) ? ! this.state.playingPodcast : true

		this.setState( {
			playingPodcast: playingPodcast,
			playingPodcastFile: data.url
		} )

		dispatch( setIsPlaying( playingPodcast ) )

		// If not already set, set the now playing data:
		if ( playingPodcast && nowPlaying.audio_file_url != data.url ) {
			dispatch( setCustomNowPlaying(
				{
					is_playlist: true,
					is_share: isShare,
					is_favourites: isFavourites,
					is_recent_episodes: isRecent,
					is_podcasts: isPodcast,
					audio_file_url: data.url || data.audio_file_url,
					seconds: parseInt( data.seconds ),
					title: data.title,
					series: data.series,
					series_slug: data.series_slug,
					guests: data.guests,
					number: data.number,
					most_recent_date: data.most_recent_date
				},
				{
					playlist: playlist.playlist,
					playlistCurrentIndex: playlist.currentIndex,
					playlistProgramData: programData
				}
			) )
		}

		if ( isMobile ) {
			history.push( '/listen' )
		}
	}

	handleLikeClick = ( e ) => {
		const file = e.currentTarget.getAttribute( 'data-file' )
		const { dispatch, isLiked, data, isFavourites, accountData, loggedIn } = this.props
		const keepInState = isFavourites

		if ( ! loggedIn ) {
			this.setState( {
				showLikeNotice: true
			} )

			return
		}

		if ( accountData && ! accountData.likes || ! Object.keys( accountData.likes ).length ) {
			this.setState( {
				showFirstLikeNotice: true
			} )
		}

		dispatch( likeProgram( {
			audio_file_url: file,
			title: data.title,
			series: data.series,
			series_slug: data.series_slug,
			guests: data.guests,
			number: data.number,
			last_played_timestamp_gmt: data.last_played_timestamp_gmt
		}, keepInState ) )

		if ( ! isLiked ) {
			this.setState( {
				clickedLike: true
			} )

			setTimeout( () => {
				this.setState( {
					clickedLike: false
				} )
			}, 700 )
		}
	}

	handleCloseLikeNotice = () => {
		this.setState( {
			showLikeNotice: false,
			showFirstLikeNotice: false
		} )
	}

	render() {
		const { data, programData, isPodcast, isLast, rewindMaxHours, nowPlaying, isPlayingGlobal, accountData, shareLinkData, isShare } = this.props
		let { isLiked, isSeriesLiked } = this.props
		const { showNotice, noticeText, showLikeNotice, showFirstLikeNotice } = this.state
		const { playingPodcast, playingPodcastFile, clickedLike } = this.state
		let className = '-program -program-podcast'

		const utcTimestampSeconds = getUtcTimestampSeconds()
		let lastPlayedSecondsAgo = utcTimestampSeconds - parseInt( data.last_played_timestamp_gmt, 10 )
		let lastPlayedHoursAgo = lastPlayedSecondsAgo / 60 / 60
		let canPlay = ( isShare || programData.podcasts_enabled || lastPlayedHoursAgo <= rewindMaxHours )
		let isPlaying = ( playingPodcast && playingPodcastFile == data.url )
		let isSelected = false
		let isShareLink = false

		if ( nowPlaying.is_playlist ) {
			isPlaying = nowPlaying.audio_file_url == data.url ? isPlayingGlobal : false
			isSelected = nowPlaying.audio_file_url == data.url
		}

		if ( null === isLiked || undefined === isLiked ) {
			if ( accountData && accountData.likes && accountData.likes[ data.url ] ) {
				isLiked = ! accountData.likes[ data.url ].is_removed
			}
		}

		if ( null === isSeriesLiked || undefined === isSeriesLiked ) {
			if ( accountData && accountData.likes_series && accountData.likes_series[ programData.series_slug ] ) {
				isSeriesLiked = true
			}
		}

		if ( shareLinkData && shareLinkData.file_name == data.audio_file_url ) {
			isShareLink = true
			className += ' is-share-link'
		}

		if ( isSelected ) {
			className += ' is-selected'
		}

		// Play:
		let playClass = '-play-item'
		let playIconClass = 'icon-playback-play'

		if ( isPlaying ) {
			playIconClass = 'icon-playback-pause'
		}

		if ( ! canPlay ) {
			playClass += ' is-disabled'
		}

		// Number:
		let numberMarkup = null

		if ( ( isPodcast || programData.podcasts_enabled ) && data.number ) {
			numberMarkup = <div className="-number">{ data.number }</div>
		} else if ( ! isPodcast && data.most_recent_date ) {
			numberMarkup = <div className="-number">{ data.most_recent_date }</div>
		}

		// Like:
		let likeClass = ( isLiked || isSeriesLiked ) ? '-like is-active' : '-like'

		if ( clickedLike ) {
			isLiked = true
			likeClass += ' is-clicked'
		}

		return (
			<div className={ className } key={ data.url }>
				{ showLikeNotice &&
					<SiteNotice
						text="Please log in or create an account to use this feature"
						cancelText="Cancel"
						handleCancel={ this.handleCloseLikeNotice }
					>
						<div className="-actions">
							<a href="/signin">Log in</a>
						</div>
					</SiteNotice>
				}

				{ showFirstLikeNotice &&
					<SiteNotice
						text="This program has been added to your favourites"
						cancelText="OK"
						handleCancel={ this.handleCloseLikeNotice }
					>
						<div className="-actions">
							<Link to="/favourites" onClick={ this.handleCloseLikeNotice }>View my favourites</Link>
						</div>
					</SiteNotice>
				}

				{ isShareLink &&
					<div className="share-link-icon icon-share" aria-hidden="true"></div>
				}

				<div className="-time-wrap">
					{ numberMarkup }

					<div className="-actions">
						<div className={ playClass } onClick={ this.handlePlayItemClick } data-file={ data.url }>
							<i className={ playIconClass } aria-hidden="true"></i>
						</div>

						{ ( isPodcast || programData.podcasts_enabled ) &&
							<a className="-download-item" href={ data.url }>
								<i className="icon-cloud-download-2" aria-hidden="true"></i>
							</a>
						||
							<div className="-download-item is-disabled">
								<i className="icon-cloud-download-2" aria-hidden="true"></i>
							</div>
						}

						<div className={ likeClass }>
							{ isLiked &&
								<i className="icon-heart" aria-hidden="true" onClick={ this.handleLikeClick } data-file={ data.url } data-title={ data.title } />
							|| isSeriesLiked &&
								<i className="icon-heart-o" aria-hidden="true" onClick={ this.handleLikeClick } data-file={ data.url } data-title={ data.title } />
							||
								<i className="icon-heart-o" aria-hidden="true" onClick={ this.handleLikeClick } data-file={ data.url } data-title={ data.title } />
							}
						</div>
					</div>
				</div>

				<div className="-text">
					<div className="-track-info-link" onClick={ this.handleShowMenuClick } />

					<div className="-title-guests">
						<h5 className="-title" dangerouslySetInnerHTML={ { __html: data.title } } />

						{ data.guests && data.guests.length > 0 &&
							<div className="-guests">
								<span className="-dash">- </span><span dangerouslySetInnerHTML={ { __html: data.guests } } />
							</div>
						}
					</div>
				</div>

				{ numberMarkup }
			</div>
		)
	}
}

const mapStateToProps = ( state, ownProps ) => {
	return {
		rewindMaxHours: state.schedule.rewindMaxHours,
		nowPlaying: state.schedule.nowPlaying,
		isPlayingGlobal: state.schedule.isPlaying,
		timezone: state.schedule.timezone,
		loggedIn: state.site.loggedIn,
		accountData: state.site.accountData,
		shareLinkData: state.site.shareLinkData,
		isMobile: state.site.isMobile
	}
}

export default withRouter(
	connect( mapStateToProps )( ProgramTrack )
)
